import type {
  CreateDuelPayload,
  DuelDistancePayload,
  DuelDto,
  DuelMoveDto,
  DuelPingPayload,
  DuelRemovalDto,
  MakeDuelMovePayload,
} from '@/core/data/duel/duel.interface';
import type { DuelRepository } from '@/core/data/duel/duel.repository';
import type { ApiError, Result } from '@/core/network/http/httpClient.interface';
import { generalError } from '@/core/network/http/httpError';

export class DuelService {
  private readonly repository: DuelRepository;

  constructor(repository: DuelRepository) {
    this.repository = repository;
  }

  async create(payload: CreateDuelPayload): Promise<Result<DuelDto>> {
    return this.run(() => this.repository.create(payload));
  }

  async join(code: string): Promise<Result<DuelDto>> {
    return this.run(() => this.repository.join(code));
  }

  /** Resolves to an empty result when the user is not in a duel. */
  async getActive(): Promise<Result<DuelDto | null>> {
    try {
      const response = await this.repository.getActive();
      return { data: response?.data ?? null };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async getMine(limit?: number): Promise<Result<DuelDto[]>> {
    return this.run(() => this.repository.getMine(limit));
  }

  async getByCode(code: string): Promise<Result<DuelDto>> {
    return this.run(() => this.repository.getByCode(code));
  }

  async getDuel(id: string): Promise<Result<DuelDto>> {
    return this.run(() => this.repository.getDuel(id));
  }

  async getMoves(id: string): Promise<Result<DuelMoveDto[]>> {
    return this.run(() => this.repository.getMoves(id));
  }

  async ready(id: string, speedMps: number): Promise<Result<DuelDto>> {
    return this.run(() => this.repository.ready(id, speedMps));
  }

  async start(id: string): Promise<Result<DuelDto>> {
    return this.run(() => this.repository.start(id));
  }

  async makeMove(id: string, payload: MakeDuelMovePayload): Promise<Result<DuelDto>> {
    return this.run(() => this.repository.makeMove(id, payload));
  }

  async ping(id: string, payload: DuelPingPayload): Promise<Result<DuelDto>> {
    return this.run(() => this.repository.ping(id, payload));
  }

  async cancel(id: string): Promise<Result<DuelDto>> {
    return this.run(() => this.repository.cancel(id));
  }

  async resign(id: string, payload: DuelDistancePayload): Promise<Result<DuelDto>> {
    return this.run(() => this.repository.resign(id, payload));
  }

  async offerDraw(id: string): Promise<Result<DuelDto>> {
    return this.run(() => this.repository.offerDraw(id));
  }

  async acceptDraw(id: string, payload: DuelDistancePayload): Promise<Result<DuelDto>> {
    return this.run(() => this.repository.acceptDraw(id, payload));
  }

  /** Leaves a finished duel behind, taking your name and your route with you. */
  async removeMe(id: string): Promise<Result<DuelRemovalDto>> {
    return this.run(() => this.repository.removeMe(id));
  }

  async declineDraw(id: string): Promise<Result<DuelDto>> {
    return this.run(() => this.repository.declineDraw(id));
  }

  private async run<T>(call: () => Promise<{ data?: T } | null>): Promise<Result<T>> {
    try {
      const response = await call();
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }
}
